import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useApiClient } from '@/api/client'
import { useWorkspace } from '@/workspaces/WorkspaceContext'

export type CreateTaskInput = {
  title: string
  projectId?: string | null
  parentTaskId?: string | null
  assigneeId?: string | null
  creatorId?: string | null
  startDate?: string
  endDate?: string
  plannedStart?: string
  plannedEnd?: string
  isMilestone?: boolean
  priority?: 'low' | 'medium' | 'high'
  customAttributes?: Record<string, unknown>
}

export type UpdateTaskInput = {
  title?: string
  description?: string
  projectId?: string | null
  assigneeId?: string | null
  creatorId?: string | null
  startDate?: string | null
  endDate?: string | null
  plannedStart?: string | null
  plannedEnd?: string | null
  isMilestone?: boolean
  priority?: 'low' | 'medium' | 'high' | null
  status?: string
  customAttributes?: Record<string, unknown>
}

export type MoveTaskInput = {
  id: string
  startDate?: string | null
  endDate?: string | null
  deltaDays: number
}

// Маппинг патча в сырые snake_case-атрибуты для оптимистичного обновления кеша
// (мгновенный отклик при move/resize на таймлайне — без отката-назад до рефетча).
function rawAttrPatch(input: UpdateTaskInput): Record<string, unknown> {
  const out: Record<string, unknown> = {}
  if (input.title !== undefined) out.title = input.title
  if (input.startDate !== undefined) out.start_date = input.startDate
  if (input.endDate !== undefined) out.end_date = input.endDate
  if (input.plannedStart !== undefined) out.planned_start = input.plannedStart
  if (input.plannedEnd !== undefined) out.planned_end = input.plannedEnd
  if (input.priority !== undefined) out.priority = input.priority
  if (input.assigneeId !== undefined) out.assignee_id = input.assigneeId
  if (input.projectId !== undefined) out.project_id = input.projectId
  return out
}

type TasksSearchCache = {
  items: Array<{ task: { id: string; attributes: Record<string, unknown> } }>
  total: number
}

function patchTaskSearchCache(
  qc: ReturnType<typeof useQueryClient>,
  slug: string,
  id: string,
  attrPatch: Record<string, unknown>,
) {
  if (Object.keys(attrPatch).length === 0) return
  qc.setQueriesData<TasksSearchCache>({ queryKey: ['tasks-search', slug] }, (old) => {
    if (!old?.items) return old
    return {
      ...old,
      items: old.items.map((item) =>
        item.task?.id === id
          ? { ...item, task: { ...item.task, attributes: { ...item.task.attributes, ...attrPatch } } }
          : item,
      ),
    }
  })
}

function toPayload(input: CreateTaskInput | UpdateTaskInput): Record<string, unknown> {
  const out: Record<string, unknown> = {}
  if ('title' in input && input.title !== undefined) out.title = input.title
  if ('description' in input && (input as UpdateTaskInput).description !== undefined) out.description = (input as UpdateTaskInput).description
  if (input.projectId !== undefined) out.project_id = input.projectId
  if ('parentTaskId' in input && input.parentTaskId !== undefined) out.parent_task_id = input.parentTaskId
  if (input.assigneeId !== undefined) out.assignee_id = input.assigneeId
  if ('creatorId' in input && input.creatorId !== undefined) out.creator_id = input.creatorId
  if (input.startDate !== undefined) out.start_date = input.startDate
  if (input.endDate !== undefined) out.end_date = input.endDate
  if (input.plannedStart !== undefined) out.planned_start = input.plannedStart
  if (input.plannedEnd !== undefined) out.planned_end = input.plannedEnd
  if (input.isMilestone !== undefined) out.is_milestone = input.isMilestone
  if (input.priority !== undefined) out.priority = input.priority
  if ('customAttributes' in input && input.customAttributes) {
    Object.assign(out, input.customAttributes)
  }
  return out
}

export function useTaskMutations() {
  const { fetchWithAuth } = useApiClient()
  const { workspace } = useWorkspace()
  const qc = useQueryClient()
  const slug = workspace.slug

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ['tasks-search', slug] })
    qc.invalidateQueries({ queryKey: ['task-detail-list', slug] })
  }

  const createTask = useMutation({
    mutationFn: (input: CreateTaskInput) =>
      fetchWithAuth<{ id: string }>(`/api/v1/workspaces/${slug}/tasks`, {
        method: 'POST',
        body: JSON.stringify(toPayload(input)),
      }),
    onSuccess: () => invalidate(),
  })

  const updateTask = useMutation({
    mutationFn: async ({ id, patch }: { id: string; patch: UpdateTaskInput }) => {
      const attrs = toPayload(patch)
      const statusSlug = patch.status
      delete attrs.status

      if (Object.keys(attrs).length > 0) {
        await fetchWithAuth(`/api/v1/workspaces/${slug}/tasks/${id}`, {
          method: 'PATCH',
          body: JSON.stringify(attrs),
        })
      }
      if (statusSlug) {
        await fetchWithAuth(`/api/v1/workspaces/${slug}/tasks/${id}/segments`, {
          method: 'POST',
          body: JSON.stringify({ status: statusSlug, started_at: new Date().toISOString() }),
        })
      }
    },
    onMutate: async ({ id, patch }: { id: string; patch: UpdateTaskInput }) => {
      const attrPatch = rawAttrPatch(patch)
      const previous = qc.getQueriesData<TasksSearchCache>({ queryKey: ['tasks-search', slug] })
      // Синхронно (в том же тике, что и отпускание мыши) патчим кеш — чтобы бар не
      // отскакивал на старые даты до рефетча. cancelQueries делаем ПОСЛЕ, иначе await
      // увёл бы setQueriesData в микротаск и вызвал прыжок.
      patchTaskSearchCache(qc, slug, id, attrPatch)
      await qc.cancelQueries({ queryKey: ['tasks-search', slug] })
      return { previous }
    },
    onError: (_error, _vars, context) => {
      context?.previous?.forEach(([key, data]) => qc.setQueryData(key, data))
    },
    onSettled: (_data, _error, variables) => {
      invalidate()
      // Смена статуса создаёт сегмент — таймлайн читает их из task-segments,
      // которые invalidate() не трогает. Инвалидируем точечно по задаче.
      if (variables?.patch?.status) {
        qc.invalidateQueries({ queryKey: ['task-segments', slug, variables.id] })
      }
    },
  })

  const moveTask = useMutation({
    mutationFn: ({ id, startDate, endDate, deltaDays }: MoveTaskInput) =>
      fetchWithAuth(`/api/v1/workspaces/${slug}/tasks/${id}/move`, {
        method: 'POST',
        body: JSON.stringify({
          start_date: startDate,
          end_date: endDate,
          delta_days: deltaDays,
        }),
      }),
    onMutate: async ({ id, startDate, endDate }: MoveTaskInput) => {
      const previous = qc.getQueriesData<TasksSearchCache>({ queryKey: ['tasks-search', slug] })
      const patch: Record<string, unknown> = {}
      if (startDate !== undefined) patch.start_date = startDate
      if (endDate !== undefined) patch.end_date = endDate
      patchTaskSearchCache(qc, slug, id, patch)
      await qc.cancelQueries({ queryKey: ['tasks-search', slug] })
      return { previous }
    },
    onError: (_error, _vars, context) => {
      context?.previous?.forEach(([key, data]) => qc.setQueryData(key, data))
    },
    onSettled: (_data, _error, variables) => {
      invalidate()
      qc.invalidateQueries({ queryKey: ['task-segments', slug, variables?.id] })
    },
  })

  const archiveTask = useMutation({
    mutationFn: (id: string) =>
      fetchWithAuth(`/api/v1/workspaces/${slug}/tasks/${id}/archive`, { method: 'POST' }),
    onSuccess: () => invalidate(),
  })

  return { createTask, updateTask, moveTask, archiveTask }
}
